import type { MutableRefObject } from 'react';
import React, { useRef, useState } from 'react';
import DigitalCardLayerStyled from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/index.styled';
import type { SpriteImageLayer } from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/types';
import { useDigitalCardConfig, useDigitalCardState } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';
import { useImageLoad } from 'src/components/_common/DigitalCard/_hooks/useImageLoad';
import { calculateSpritePositions } from 'src/utils/image';
import styled from 'styled-components';

import { useAnimation } from './_hook/useAnimation';

const DEFAULT_DURATION = 6;
const DEFAULT_SCALE = 1;

/**
 * [스프라이트 이미지 레이어 컴포넌트]
 *
 * @description
 * 스프라이트 이미지 레이어를 렌더링하는 컴포넌트입니다.
 *
 * @param {SpriteImageLayer} props - 스프라이트 이미지 레이어 컴포넌트의 속성
 * ```ts
 * {
 *   idx: number; // 레이어의 고유 식별자
 *   name: string; // 레이어의 이름
 *   type: 'spriteImage'; // 레이어의 타입
 *   zIndex: number; // 레이어의 z-index 값
 *   translateZ: number; // 레이어의 z축 변환 값
 *   spriteWidth: number; // 스프라이트 이미지의 너비
 *   spriteHeight: number; // 스프라이트 이미지의 높이
 *   spriteFrameCount: number; // 스프라이트 이미지의 프레임 수
 *   spriteImage: string; // 스프라이트 이미지의 경로
 *   duration: number; // 애니메이션 지속 시간 (기본값: 6)
 *   scale?: number; // 스프라이트 이미지의 크기 배율 (기본값: 1)
 *   translate?: string; // 스프라이트 이미지의 위치 변환 값 (기본값: 'translate(-50%, -50%)')
 * }
 * ```
 */
export const DigitalCardSpriteImageLayer = ({
  zIndex,
  translateZ,
  spriteWidth,
  spriteHeight,
  spriteFrameCount,
  spriteImage,
  duration = DEFAULT_DURATION,
  scale = DEFAULT_SCALE,
  translate,
}: SpriteImageLayer) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const spriteImageRef = useRef<HTMLImageElement | null>(null) as MutableRefObject<HTMLImageElement | null>;
  const [isSpriteLoaded, setIsSpriteLoaded] = useState(false);

  const { spriteImageLoadRetryCount, spriteImageLoadDelayTime } = useDigitalCardConfig();
  const { isAllResourcesLoaded } = useDigitalCardState();

  useImageLoad({
    imageRef: spriteImageRef,
    useImageObject: true,
    src: spriteImage,
    onLoadComplete: () => setIsSpriteLoaded(true),
    retryCount: spriteImageLoadRetryCount,
    delayTime: spriteImageLoadDelayTime,
  });

  // 스프라이트 시트의 각 프레임 좌표 계산
  const coordinates = calculateSpritePositions({
    w: spriteWidth,
    h: spriteHeight,
    count: spriteFrameCount,
  });

  // 프레임 애니메이션 처리
  useAnimation({
    isReady: isAllResourcesLoaded && isSpriteLoaded,
    coordinates,
    frameCount: spriteFrameCount,
    duration,
    width: spriteWidth,
    height: spriteHeight,
    canvasRef,
    spriteImageRef,
  });

  return (
    <SpriteImageLayerWrapper $zIndex={zIndex} $translateZ={translateZ}>
      <CanvasContainer $width={spriteWidth} $height={spriteHeight} $scale={scale} $translate={translate}>
        <canvas
          ref={canvasRef}
          width={spriteWidth}
          height={spriteHeight}
          style={{
            opacity: isAllResourcesLoaded && isSpriteLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            width: '100%',
            aspectRatio: `${spriteWidth}/${spriteHeight}`,
          }}
        />
      </CanvasContainer>
    </SpriteImageLayerWrapper>
  );
};

const SpriteImageLayerWrapper = styled(DigitalCardLayerStyled.BaseLayerWrapper)`
  overflow: visible;
  pointer-events: none;
`;

const CanvasContainer = styled.div<{
  $width?: number;
  $height?: number;
  $scale?: number;
  $translate?: string;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: ${({ $translate, $scale }) =>
    `${$translate ? `translate(${$translate})` : 'translate(-50%, -50%)'}${$scale ? ` scale(${$scale})` : ''}`};
  width: 100%;
  aspect-ratio: ${({ $width, $height }) => `${$width}/${$height}`};
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
`;
